// Blog list — editorial Fieldbook treatment. Self-contained chrome, routed
// standalone at /blog. Wired to the real virtual:blog-manifest. Prerendered.
import { useState } from "react";
import { Link } from "react-router-dom";
import allPosts from "virtual:blog-manifest";
import { getHeroImage, type BlogMeta } from "@/lib/blog";
import { PageMeta } from "@/components/PageMeta";
import { canonicalUrl } from "@/lib/canonical";
import FieldHeader from "@/components/field/FieldHeader";
import FieldFooter from "@/components/field/FieldFooter";
import "@/styles/design-tokens.css";
import "./blog.css";

const POSTS_PER_PAGE = 12;

const voiceLabels: Record<string, string> = {
  technical: "Technical",
  "ai-expert": "AI & ML",
  legal: "Compliance",
  marketing: "Product",
};

function voiceLabel(v: string): string {
  return voiceLabels[v] || v;
}

function fmtDate(d: string): string {
  return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function hideOnError(e: React.SyntheticEvent<HTMLImageElement>) {
  (e.target as HTMLImageElement).style.visibility = "hidden";
}

export default function BlogPage() {
  const [visibleCount, setVisibleCount] = useState(POSTS_PER_PAGE);
  const posts = allPosts as BlogMeta[];
  const visible = posts.slice(0, visibleCount);
  const featured = visible[0];
  const rest = visible.slice(1);
  const hasMore = visibleCount < posts.length;

  return (
    <div className="ffly ffly--public blog-page">
      <PageMeta
        title="Blog"
        description="Insights on healthcare APIs, FHIR interoperability, SMART Health Links, MCP, and building better clinical data integrations. Deep dives from the FHIRfly team."
        canonical={canonicalUrl("/blog")}
      />
      <FieldHeader active="blog" />

      <div className="wrap">
        <div className="masthead">
          <div>
            <div className="eyebrow">Field Notes</div>
            <h1>The FHIRfly Blog</h1>
            <p className="sub">Healthcare APIs, FHIR interoperability, and building better clinical data integrations.</p>
          </div>
          <a className="rss" href="/rss.xml" target="_blank" rel="noopener noreferrer">↯ RSS Feed</a>
        </div>

        {posts.length === 0 && (
          <div className="empty">No posts yet — check back soon.</div>
        )}

        {featured && (
          <Link className="featured" to={`/blog/${featured.slug}`}>
            <div className="fig"><img src={getHeroImage(featured.slug)} alt="" onError={hideOnError} /></div>
            <div>
              <div className="meta"><span className="tag">{voiceLabel(featured.voice)}</span><span>{fmtDate(featured.publishedAt)}</span><span>Latest</span></div>
              <h2>{featured.title}</h2>
              <p className="ex">{featured.excerpt}</p>
              <span className="readmore">Read article →</span>
            </div>
          </Link>
        )}

        {rest.length > 0 && (
          <div className="grid">
            {rest.map((p) => (
              <Link className="card" to={`/blog/${p.slug}`} key={p.slug}>
                <div className="fig"><img src={getHeroImage(p.slug)} alt="" loading="lazy" onError={hideOnError} /></div>
                <div className="meta"><span className="tag">{voiceLabel(p.voice)}</span><span>{fmtDate(p.publishedAt)}</span></div>
                <h3>{p.title}</h3>
                <p className="ex">{p.excerpt}</p>
              </Link>
            ))}
          </div>
        )}

        {posts.length > 0 && (
          <div className="more-wrap">
            <p className="count">Showing {Math.min(visibleCount, posts.length)} of {posts.length} posts</p>
            {hasMore && (
              <button className="more" onClick={() => setVisibleCount((c) => c + POSTS_PER_PAGE)}>Show more</button>
            )}
          </div>
        )}
      </div>

      <FieldFooter variant="full" />
    </div>
  );
}
